import { faArrowLeft, faX } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Link from 'next/link'
import React from 'react'
import InlineSpinner from '@/components/Layout/InlineSpinner'
import { formatBRL } from '~/helpers/formatCurrency'
import type { ICart } from '~/schemas/cart/cart'

interface ICartSummaryProps {
    cart: ICart
    /** Todos os itens com estoque: libera o "Finalizar Pedido". */
    canCheckout: boolean
    /** Escolha de frete em voo: o frete mostra o indicador em vez do valor antigo. */
    isShippingPending?: boolean
    /** A cotação dos itens de agora falhou: o frete guardado não vale, então não aparece nem entra no Total. */
    shippingFailed?: boolean
    onOpenCoupon: () => void
    onRemoveCoupon: () => void
    onCheckout: () => void
}

/** Resumo do carrinho: subtotal, frete, cupom, total e os botões de finalizar e continuar comprando. */
const CartSummary: React.FC<ICartSummaryProps> = ({
    cart,
    canCheckout,
    isShippingPending = false,
    shippingFailed = false,
    onOpenCoupon,
    onRemoveCoupon,
    onCheckout,
}) => {
    const subTotalPrice = cart.cartItems.reduce((acc, item) => acc + item.product.discountedPrice * item.quantity, 0)

    return (
        <>
            <div className="pb-3 *:not-first:mt-3 border-b border-submenu-gray">
                <p className="mb-2 mr-4 text-xl font-extrabold max-lg:text-md text-destaques-hover font-sora">
                    Subtotal
                </p>
                <div className="flex justify-between">
                    <p className="text-sm text-black">{cart.cartItems.length} produtos no carrinho</p>
                    <span className="text-sm font-bold text-destaques-hover font-sora">{formatBRL(subTotalPrice)}</span>
                </div>
                <div className="flex justify-between ">
                    <p className="text-sm text-black">Frete total</p>
                    <span className="text-sm font-bold text-destaques-hover font-sora">
                        {!cart.cep ? (
                            'Informe o CEP'
                        ) : isShippingPending ? (
                            <InlineSpinner size={14} />
                        ) : shippingFailed ? (
                            'A calcular'
                        ) : (
                            formatBRL(cart.shippingTax || 0)
                        )}
                    </span>
                </div>

                {cart.discountApplied ? (
                    <>
                        <div className="flex justify-between">
                            <p className="text-sm text-black">Desconto (Pedido)</p>
                            <span className="text-sm font-bold text-red font-sora">- {formatBRL(cart.discount)}</span>
                        </div>
                        <div className="items-center justify-between lg:flex">
                            <p className="text-sm text-black">Cupom de desconto</p>
                            <div className="flex items-center justify-evenly">
                                <span className="mr-2 text-sm font-normal text-destaques-hover font-sora">
                                    <span className="font-bold">{cart.discountApplied.discountCode}</span> {'('}{' '}
                                    {cart.discountApplied.name} {')'}
                                </span>
                                <button type="button" aria-label="Remover o cupom" onClick={onRemoveCoupon}>
                                    <FontAwesomeIcon
                                        icon={faX}
                                        color="#fff"
                                        className="h-2 p-1 text-gray-400 border rounded-full cursor-pointer border-light-black hover:border-primary hover:text-primary text-bold"
                                    />
                                </button>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="flex justify-between">
                        <p className="text-sm text-black">Cupom de desconto</p>
                        <button
                            type="button"
                            onClick={onOpenCoupon}
                            className="px-2 py-1 text-xs text-gray-400 border rounded-full font-sora border-submenu-gray hover:text-destaques-hover hover:border-destaques-hover"
                        >
                            Adicionar
                        </button>
                    </div>
                )}
            </div>

            <div className="flex flex-col items-center gap-4">
                <div className="flex justify-between w-full">
                    <p className="text-2xl font-bold text-destaques-hover font-sora">Total</p>
                    <span className="text-2xl font-bold text-destaques-hover font-sora">
                        {formatBRL((cart.total || 0) - (shippingFailed ? cart.shippingTax || 0 : 0))}
                    </span>
                </div>

                <button
                    type="button"
                    onClick={onCheckout}
                    className={`w-full py-2 mb-2 text-lg font-bold text-center text-white uppercase rounded-full bg-healthy-green hover:bg-primary-green disabled:bg-gray ${canCheckout ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                    disabled={!canCheckout}
                >
                    Finalizar Pedido
                </button>
                <Link href="/produtos" className="text-lg uppercase text-orange hover:text-salmon -mb-60">
                    <FontAwesomeIcon icon={faArrowLeft} /> Continuar Comprando
                </Link>
            </div>
        </>
    )
}

export default CartSummary
